import { useCallback, useState } from 'react';
import { Box, IconButton, useTheme } from '@mui/material';
import { WidgetWrapper } from '../WidgetWrapper';
import { AssetControllerWidgetPopup } from '../AssetControllerWidgetPopup/AssetControllerWidgetPopup';
import { useToggle } from 'src/hooks/useToggle';
import { WidgetSubTypeEnum, WidgetType } from 'src/features/sites/types/types';
import { AssetResponse } from 'src/types/assets';
import { DosingPumpIcon } from 'src/assets/icons/DosingPumpIcon';
import { PumpIcon } from 'src/assets/icons/PumpIcon';
import { CRANE_PUMP, PUMP_1, PUMP_2, PUMP_3 } from 'src/features/sites';
import { PropelerPumpIcon } from '../PropelerPumpIcon';
import { CranePumpWidget } from 'src/assets/widgets/CranePumpWidget';
import { LabelAndValue } from './components/LabelAndValue';
import { useWebSockets } from 'src/contexts/webSocketsContext/websockets.context';
import { useLiveData } from '../LiftStationWidget/hooks';
import { TagResponse } from 'src/types/tags/tags';
import { usePumpData } from './hooks/usePumpData';
import { ControllerElement } from '../ControllerElement';

import { WetWellWidget } from './components/WetWellWidget';
import { RegularPumpWithPropeler } from '../RegularPumpWithPropeler/RegularPumpWithPropeler';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { ValveAssetControl } from './components/ValveAssetControl';
import { useAssetStateData } from 'src/features/sites/hooks';

type Props = {
  widget: WidgetType;
  toggleAddOrRemoveHandler: () => void;
  asset?: AssetResponse;
  gatewayInputs: TagResponse[];
  isEditMode: boolean;
  waterLevel: number | null;
  height: number;
  unit: string;
};

type TagType = {
  id: string;
  label: string;
  tagName: null | string;
  position: number;
};

export const AssetController = ({
  widget,
  toggleAddOrRemoveHandler,
  asset,
  gatewayInputs,
  isEditMode,
  waterLevel,
  unit,
  height,
}: Props) => {
  const { state, onToggle } = useToggle();
  const [controllerState, setControllerState] = useState(false);
  const widgetSubType = widget?.widgetSubType;
  const widgetId = widget?.id;
  const tags = widget?.connectedTo?.liveDataTags;
  const showAlarmStatus = widget?.connectedTo?.showAlarmStatus;
  const controllerId = widget?.connectedTo?.controllerId;
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { messages } = useWebSockets();

  const isPumpWidget = asset?.assetType === 'PUMP';
  const {
    assetState: liveActiveState,
    assetActiveAlarm,
    isRunningLive,
  } = useAssetStateData({ asset: asset! });

  const { formttedTags, liveDataComplete } = useLiveData({
    selectedTagIds: tags,
    gatewayInputs: gatewayInputs,
    messages: messages,
  });

  const renderWidgetIcon = (isRunMode: boolean, isAlarmActive: boolean) => {
    const iconWidth = 70;
    const iconHeight = 70;
    if (widgetSubType === PUMP_1)
      return (
        <RegularPumpWithPropeler
          width={90}
          height={90}
          propelerWidth={25}
          isAlarmActive={isAlarmActive}
          isRunMode={isRunMode}
        />
      );
    if (widgetSubType === PUMP_2) return <DosingPumpIcon width={iconWidth} height={iconHeight} />;
    if (widgetSubType === PUMP_3)
      return (
        <PropelerPumpIcon
          isRunMode={isRunMode}
          isAlarmActive={isAlarmActive}
          showRotator={true}
          isMobile={false}
          isTriplexPump={false}
        />
      );
    if (widgetSubType === CRANE_PUMP) return <CranePumpWidget width={50} height={90} />;
    if (widgetSubType === WidgetSubTypeEnum.WET_WELL_1)
      return <WetWellWidget asset={asset} waterLevel={waterLevel} unit={unit} height={height} />;

    if (widgetSubType?.includes('VALVE'))
      return <ValveAssetControl widgetSubType={widgetSubType} asset={asset} />;

    return <PumpIcon />;
  };

  //Pump
  const { isPumpMappedToAlarmTags, isRunning, isAlarmActive, isReady } = usePumpData({
    asset: asset,
    liveActiveState: liveActiveState,
    assetActiveAlarm: assetActiveAlarm,
  });

  const hasTags = Boolean(tags?.length);
  const areTagsReady = hasTags ? Boolean(formttedTags?.length) : true;
  const currentIsRunning = isRunningLive ?? isRunning;

  const handleToggleController = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!controllerId) {
        enqueueSnackbar('For this asset, control is not set up.', {
          variant: 'default',
          autoHideDuration: 3000,
          content: (key, message) => (
            <div
              id={String(key)}
              style={{
                backgroundColor: theme.palette.grey[500],
                color: 'white',
                padding: '8px 16px',
                borderRadius: 4,
              }}
            >
              <span>{message}</span>
              <IconButton
                size="small"
                onClick={() => closeSnackbar(key)}
                style={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          ),
        });
        return;
      }

      setControllerState((prev) => !prev);
    },
    [closeSnackbar, controllerId, enqueueSnackbar, theme.palette.grey]
  );

  return (
    <>
      <WidgetWrapper
        toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
        isEditMode={isEditMode}
        widgetId={widgetId!}
        title={asset?.assetName || ''}
        isDataReady={isPumpWidget ? isReady && areTagsReady : areTagsReady}
        isAssetController
        onEdit={onToggle}
        isPumpMappedToAlarmTags={isPumpWidget && showAlarmStatus ? isPumpMappedToAlarmTags : false}
        isPumpWidget={isPumpWidget}
        isAlarmForPumpActive={isPumpWidget ? isAlarmActive : false}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                onClick={handleToggleController}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  pointerEvents: 'auto',
                }}
              >
                <Box sx={{ pointerEvents: 'none' }}>
                  {renderWidgetIcon(currentIsRunning, isAlarmActive)}
                </Box>
              </Box>
            </Box>

            <Box>
              {tags?.map((tag: TagType) => (
                <LabelAndValue
                  key={tag.id}
                  id={tag.id}
                  label={tag.label}
                  liveDataComplete={liveDataComplete}
                  restTags={formttedTags}
                />
              ))}
            </Box>
          </Box>
          {controllerId && (
            <Box sx={{ alignSelf: 'end', pb: '0.5rem' }}>
              <ControllerElement
                controllerId={controllerId}
                hideText
                isOpen={controllerState}
                setControllerState={setControllerState}
              />
            </Box>
          )}
        </Box>
      </WidgetWrapper>

      <AssetControllerWidgetPopup
        isOpen={state}
        onToggle={onToggle}
        selectedAssetForAssetController={asset?.id!}
        isEdit={true}
        existingWidget={widget}
        toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      />
    </>
  );
};
